
import * as THREE from "../../../libs/three.js/build/three.module.js";

import {t} from "../i18n/index.js";
import {hiddenClassLookup} from "./classFilter.js";
// Deterministic, camera/LOD-independent point extraction from a Potree
// octree for a polygon region-of-interest ("Areal og volumen").
//
// ── Fase 7 rewrite: bounded, single-frontier ROI loading ───────────────────
//
// The previous version force-loaded EVERY node at EVERY depth that
// intersected the ROI's bounding box (parent, child, grandchild, ... all at
// once). Confirmed against this project's own octree loader
// (`src/modules/loader/2.0/OctreeLoader.js`): nodes in this format ARE
// disjoint (a node's own points are never a resample/duplicate of its
// children's), so that approach was not *double-counting* the same points -
// but it WAS fetching full native-resolution point density everywhere the
// ROI touched, with no bound on how many nodes/points that could be for a
// dense real scan, and it forced hundreds of small sequential/gated
// network+decode round-trips regardless of whether that much raw detail was
// ever going to matter once `surfaceReduction.js` coarsens it back down to
// a robust-spacing grid anyway. That is the root cause of multi-minute
// hangs found via live testing.
//
// The fix (task section 3-5): pick exactly ONE node per spatial branch to
// treat as the analysis frontier, chosen deterministically from a fixed
// POINT BUDGET, using ONLY cheap hierarchy metadata (`node.numPoints`,
// populated by a `hierarchy.bin` range-fetch - see `OctreeLoader.js`'s
// `parseHierarchy()` - NOT the point payload itself) to decide how deep to
// descend. The SAME target depth is used uniformly across the whole ROI
// (never "first N points", which would bias toward whichever branch happens
// to be visited first); a branch that runs out of native tree depth before
// reaching that target simply contributes its own real leaf.
//
// ── Why this is camera/LOD-independent (confirmed, not assumed) ───────────
//
// `viewer.scene.pointclouds[i]` (constructor `PointCloudOctree`) exposes
// `.pcoGeometry.root`, a tree of `OctreeGeometryNode`s. This is a completely
// separate structure from `pointcloud.visibleNodes`/`Potree.pointBudget`
// (the camera-frustum-driven, per-frame rendering decision) - nothing here
// ever reads `visibleNodes`, the active camera, or the render `pointBudget`.
// The frontier-selection budget below (`DEFAULT_POINT_BUDGET`) is a
// completely separate, ROI-analysis-only concept from Potree's own render
// `pointBudget` and is never influenced by it.
//
// `node.load()` force-loads a SPECIFIC node's point data (byte-range fetch
// + worker decode) regardless of whether the renderer would ever have shown
// it. Two things confirmed live and handled below:
//   1. `node.load()` is a silent no-op (not a queued/deferred retry) while
//      `Potree.numNodesLoading >= Potree.maxNodesLoading` - `ensurePointsLoaded()`
//      re-issues it on every poll tick until it actually starts.
//   2. `node.load()` itself resolves once the request is *dispatched*, not
//      once decoding finishes - actual `node.loaded = true` happens later,
//      inside the decoder worker's own `onmessage` - so this polls
//      `node.loaded` rather than relying on any returned promise.
//   3. A node's `.children` are only populated once ITS OWN hierarchy chunk
//      has been parsed (`nodeType !== 2`) - `ensureHierarchyLoaded()` below
//      calls `NodeLoader.loadHierarchy()` DIRECTLY (bypassing `.load()`'s
//      full point-payload fetch+decode) to discover a node's children and
//      their `numPoints` cheaply, before committing to loading real point
//      data for anything.
//
// Node/point coordinates are LOCAL to the pointcloud object; the world
// position is `decodedLocalPosition (Float32) + pointcloud.position`
// (confirmed live against real decoded points, matches production usage in
// `viewer/profile.js`). `pointcloud` here has an identity-rotation/scale
// `matrixWorld` (translation only) - this simple offset add is exact for
// that case; a pointcloud placed with rotation/scale would need the full
// `matrixWorld` transform instead (not needed for this project's current
// data, documented as a known assumption rather than silently baked in).

const MAX_LOAD_WAIT_MS = 60000;
const LOAD_POLL_MS = 20;

// Nested-raise bookkeeping for the `Potree.maxNodesLoading` gate (see its
// own use below) - module-level since concurrent loadRoiPoints() calls
// share the same single global gate.
let raiseDepth = 0;
let valueBeforeAnyRaise = null;

// Deterministic point budget for the analysis frontier (task section 4).
// This is an EVALUATION PERFORMANCE PARAMETER, not a scientific accuracy
// threshold: it bounds how many raw points get fetched/decoded for a single
// volume computation, chosen so the already-benchmarked pipeline (Fase 5.5:
// low single-digit seconds up to 1M raw points) stays comfortably fast even
// after ROI loading. Documented, logged every run, and expected to be
// revisited once more real point clouds have been tested against it.
export const DEFAULT_POINT_BUDGET = 500000;

export async function ensureHierarchyLoaded(node){
	if(node.nodeType !== 2) return; // already expanded - children/numPoints known
	if(!node.__hierarchyPromise){
		node.__hierarchyPromise = node.octreeGeometry.loader.loadHierarchy(node);
	}
	await node.__hierarchyPromise;
}

export async function ensurePointsLoaded(node){
	if(node.loaded) return;
	const start = Date.now();
	while(!node.loaded){
		if(!node.loading) node.load();
		await new Promise((resolve) => setTimeout(resolve, LOAD_POLL_MS));
		if(Date.now() - start > MAX_LOAD_WAIT_MS){
			throw new Error(t("Tidsudløb ved indlæsning af punktsky-node \"{0}\".", node.name));
		}
	}
}

function intersects(node, localBox){
	return node.boundingBox.intersectsBox(localBox);
}

function boxVolume(box){
	const size = box.max.clone().sub(box.min);
	return Math.max(size.x, 1e-9) * Math.max(size.y, 1e-9) * Math.max(size.z, 1e-9);
}

function overlapVolume(boxA, boxB){
	const dx = Math.max(0, Math.min(boxA.max.x, boxB.max.x) - Math.max(boxA.min.x, boxB.min.x));
	const dy = Math.max(0, Math.min(boxA.max.y, boxB.max.y) - Math.max(boxA.min.y, boxB.min.y));
	const dz = Math.max(0, Math.min(boxA.max.z, boxB.max.z) - Math.max(boxA.min.z, boxB.min.z));
	return dx * dy * dz;
}

// A node's OWN points are spread across its OWN full bounding box, not
// concentrated where it happens to overlap a small ROI - found via live
// testing to matter a great deal: a ~4x5m polygon against real octree
// nodes whose own boxes were far larger stopped (per the naive version of
// this function, which just summed raw `numPoints`) at a level whose total
// node point count comfortably fit the budget, yet delivered almost no
// points inside the actual polygon (377,496 points in the selected nodes,
// only 135 left after the bounding-box prefilter, 0 after the exact
// polygon filter) - the budget was being satisfied almost entirely by
// points OUTSIDE the small ROI. Weighting each node's contribution by how
// much of ITS OWN volume actually overlaps the ROI (assuming a roughly
// uniform point distribution within a node - a proxy, not a precise
// density model) fixes this: a small ROI now keeps descending past
// oversized, mostly-irrelevant nodes until it reaches ones that actually
// cover it well.
function sumEstimatedPointsInRoi(nodes, localBox){
	let total = 0;
	for(const n of nodes){
		const ov = overlapVolume(n.boundingBox, localBox);
		if(ov <= 0) continue;
		total += Number(n.numPoints || 0) * (ov / boxVolume(n.boundingBox));
	}
	return total;
}

function sumPoints(nodes){
	let total = 0;
	for(const n of nodes) total += Number(n.numPoints || 0);
	return total;
}

// Task section 20: this loader's world-position formula
// (`decoded local + node.boundingBox.min + pointcloud.position`) is only
// exact for a TRANSLATION-ONLY pointcloud transform (confirmed for this
// project's current data). A pointcloud placed with rotation or non-unit
// scale would need the full `matrixWorld` transform instead - rather than
// silently producing wrong coordinates for that case, this throws a clear,
// explicit error. Not implemented as a larger redesign this iteration
// (task section 20: "ikke som et stort redesign").
const TRANSFORM_IDENTITY_EPS = 1e-6;

export function assertTranslationOnlyTransform(pointcloud){
	pointcloud.updateMatrixWorld(true);
	const position = new THREE.Vector3(), quaternion = new THREE.Quaternion(), scale = new THREE.Vector3();
	pointcloud.matrixWorld.decompose(position, quaternion, scale);

	const rotationOk = quaternion.angleTo(new THREE.Quaternion()) < TRANSFORM_IDENTITY_EPS;
	const scaleOk = Math.abs(scale.x - 1) < TRANSFORM_IDENTITY_EPS
		&& Math.abs(scale.y - 1) < TRANSFORM_IDENTITY_EPS
		&& Math.abs(scale.z - 1) < TRANSFORM_IDENTITY_EPS;

	if(!rotationOk || !scaleOk){
		throw new Error(t("Mål areal og volumen understøtter i øjeblikket kun punktskyer uden rotation eller skalering (kun forskydning). Denne punktsky har en anden transformation og kan ikke måles korrekt med dette værktøj endnu."));
	}
}

// Deterministic frontier selection (task section 3-5). Returns
// {frontier: OctreeGeometryNode[], level, totalPoints, totalPointsRaw,
// levels, budgetExceededAtRoot}. `totalPoints` is the overlap-weighted
// ESTIMATE actually used for the budget decision; `totalPointsRaw` is the
// unweighted sum (the metric the original, since-fixed version of this
// function used) - both logged so the difference itself is visible.
// `levels` is a per-depth trace purely for logging/diagnostics.
export async function selectFrontier(root, localBox, pointBudget){
	let frontier = [root];
	let level = 0;
	let bestFrontier = null;
	let bestTotal = 0;
	let bestLevel = 0;
	let budgetExceededAtRoot = false;
	const levels = [];

	while(true){
		const active = frontier.filter((n) => intersects(n, localBox));
		const total = sumEstimatedPointsInRoi(active, localBox);
		const totalRaw = sumPoints(active);
		levels.push({level, nodeCount: active.length, totalPoints: total, totalPointsRaw: totalRaw});

		if(total > pointBudget){
			if(level === 0){
				// can't go coarser than the root itself - accept it, but flag
				// this clearly so the caller can warn (task section 4: "hvis
				// 500k viser sig forkert... begrund en anden værdi")
				bestFrontier = active;
				bestTotal = total;
				bestLevel = level;
				budgetExceededAtRoot = true;
			}
			break;
		}

		bestFrontier = active;
		bestTotal = total;
		bestLevel = level;

		// try to descend one level, uniformly, across every active branch
		await Promise.all(active.map((n) => ensureHierarchyLoaded(n)));

		let next = [];
		let anyDescended = false;
		for(const n of active){
			const childKeys = Object.keys(n.children || {});
			if(childKeys.length === 0){
				next.push(n); // branch has bottomed out at its own native leaf
			}else{
				anyDescended = true;
				for(const k of childKeys) next.push(n.children[k]);
			}
		}

		if(!anyDescended) break; // whole ROI has bottomed out - nothing deeper to fetch
		frontier = next;
		level++;
	}

	return {frontier: bestFrontier, level: bestLevel, totalPoints: bestTotal, levels, budgetExceededAtRoot};
}

// Loads the point PAYLOAD (not just hierarchy) for exactly the given
// frontier nodes, in parallel (found via live testing to matter a lot -
// awaiting one node's full decode before starting the next turned a
// sub-second job into a multi-second one even for a small node count).
async function loadFrontierPoints(frontierNodes){
	await Promise.all(frontierNodes.map((n) => ensurePointsLoaded(n)));
}

// Collects a node's own decoded points into `out` (world xyz, interleaved),
// keeping only those that fall within `localBox` (task section 7's bbox
// pre-filter) - a node's own bounding box can extend beyond the padded ROI
// on branches that only partially overlap it, so this discards the part of
// each node's points that doesn't actually matter before the buffer is
// built for Worker transfer. Returns {nodePoints, keptPoints}.
//
// ── Critical fix, found via live testing ───────────────────────────────────
// A node's decoded `position` buffer is relative to THAT NODE'S OWN
// `boundingBox.min`, NOT the pointcloud's shared local origin - confirmed
// empirically: every point decoded from a given node fell in exactly
// [0, nodeSize] regardless of where that node's own bounding box actually
// sits in the tree. The earlier "world = local + pointcloud.position"
// formula (this project's own prior research) was only ever verified
// against the ROOT node, where `boundingBox.min` happens to be (0,0,0) -
// coincidentally hiding this bug there. For every other node, that formula
// silently collapsed all points toward the pointcloud's local origin,
// which is why a real polygon anywhere except right at that corner found
// zero points inside it. `node.boundingBox.min` must be added back in.
// `colorsOut`, when given, additionally collects each kept point's own
// r,g,b (0-255, from the node's 'rgba' attribute - see OctreeLoader.js's
// own `geometry.setAttribute('rgba', new THREE.BufferAttribute(new
// Uint8Array(buffer), 4, true))`) in the exact same per-point order as
// `out` - purely additive (Tværprofiler's own colour-coded profile charts,
// see crossSectionTool.js), every existing caller that only reads
// `positions` (Areal og volumen) is unaffected. A node whose data has no
// colour channel falls back to a flat neutral grey rather than throwing.
// `classFilter`, when given ({hidden: lookup per code, excluded: Set}), leaves out points of the hidden classification codes and records
// which codes actually lost points. A node without classification values keeps all its points.
export function collectNodePoints(node, offset, localBox, out, colorsOut = null, classFilter = null){
	const posAttr = node.geometry && node.geometry.attributes && node.geometry.attributes.position;
	if(!posAttr) return {nodePoints: 0, keptPoints: 0};
	const array = posAttr.array;
	const rgbaAttr = node.geometry && node.geometry.attributes && node.geometry.attributes.rgba;
	const rgba = rgbaAttr ? rgbaAttr.array : null;
	const classAttr = classFilter && node.geometry.attributes.classification;
	const classes = classAttr && classAttr.potree ? classAttr.potree.preciseBuffer : null;
	const n = array.length / 3;
	const nodeMin = node.boundingBox.min;
	let kept = 0;
	for(let i = 0; i < n; i++){
		const lx = array[i * 3] + nodeMin.x, ly = array[i * 3 + 1] + nodeMin.y, lz = array[i * 3 + 2] + nodeMin.z;
		if(lx < localBox.min.x || lx > localBox.max.x) continue;
		if(ly < localBox.min.y || ly > localBox.max.y) continue;
		if(lz < localBox.min.z || lz > localBox.max.z) continue;
		if(classes && classFilter.hidden[classes[i]]){
			classFilter.excluded.add(classes[i]);
			continue;
		}
		out.push(lx + offset.x, ly + offset.y, lz + offset.z);
		if(colorsOut){
			if(rgba) colorsOut.push(rgba[i * 4], rgba[i * 4 + 1], rgba[i * 4 + 2]);
			else colorsOut.push(160, 160, 160);
		}
		kept++;
	}
	return {nodePoints: n, keptPoints: kept};
}

// pointcloud: a `PointCloudOctree` from `viewer.scene.pointclouds`.
// polygonWorld: [{x,y,z}, ...] - the digitized polygon ring, world coords.
// marginRatio: fractional XY margin added around the polygon's own bounding
// box before querying the octree (a cheap, approximate prefilter - the exact
// (u,v) polygon filtering happens later, in the Worker's local reference-
// plane frame - see volumeWorker.js). This margin only protects against
// missing boundary-adjacent points due to floating-point edge effects.
// pointBudget: see DEFAULT_POINT_BUDGET above.
// jobId: shared job label (volumeJobId.js) for console tracing.
// hiddenClasses: classification codes to leave out (see classFilter.js).
//
// Returns {positions: Float64Array (world x,y,z interleaved), colors:
// Uint8Array (r,g,b interleaved, same per-point order as `positions` - see
// collectNodePoints()'s own doc comment), nodesLoaded, nodesConsidered,
// frontierLevel, budgetExceededAtRoot, pointBudget, nodePointCount,
// bboxFilteredPointCount, rawPointCount, excludedClasses, ms}. excludedClasses: the codes that actually lost points here.
// Multiplier applied to the estimated point spacing to size the 3D
// reconstruction margin (task section 17/section 13 of the FULL-3D task):
// MeshLib's local-neighbourhood reconstruction (default numNeighbours=16 -
// see pointCloudReconstruction.js) naturally recedes from the raw point
// cloud's own edge by roughly half a point-spacing (confirmed via this
// phase's own ground-truth testing); a margin of several spacings gives
// every point near the polygon boundary a full neighbourhood on all sides
// well before reconstruction's own natural recession would reach the true
// edge. meshCrop.js then trims the reconstructed surface back down to the
// EXACT polygon prism regardless of how far this margin extended, so a
// generous margin costs a little extra reconstruction work, never
// correctness.
const RECONSTRUCTION_MARGIN_SPACING_MULTIPLIER = 8;

export async function loadRoiPoints(pointcloud, polygonWorld, {marginRatio = 0.02, pointBudget = DEFAULT_POINT_BUDGET, jobId = 0, hiddenClasses = []} = {}){
	const t0 = performance.now();
	const tag = `[Volume job ${jobId}]`;

	assertTranslationOnlyTransform(pointcloud);

	let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
	for(const p of polygonWorld){
		if(p.x < minX) minX = p.x; if(p.x > maxX) maxX = p.x;
		if(p.y < minY) minY = p.y; if(p.y > maxY) maxY = p.y;
	}
	const baseMarginX = Math.max(1e-6, (maxX - minX) * marginRatio);
	const baseMarginY = Math.max(1e-6, (maxY - minY) * marginRatio);

	const offset = pointcloud.position;
	const root = pointcloud.pcoGeometry.root;

	// full vertical extent of the whole point cloud (in local/node space) -
	// this ROI query is deliberately unrestricted in Z (an excavation floor
	// or a pile's crest can be far from the digitized rim's own Z); only the
	// XY footprint is restricted to the polygon's own bounding box. This is
	// an intentionally separate question from the LOD/frontier depth chosen
	// below - full-Z does not imply full-depth.
	function makeLocalBox(marginX, marginY){
		return new THREE.Box3(
			new THREE.Vector3(minX - marginX - offset.x, minY - marginY - offset.y, root.boundingBox.min.z),
			new THREE.Vector3(maxX + marginX - offset.x, maxY + marginY - offset.y, root.boundingBox.max.z),
		);
	}

	console.log(`${tag} ROI: polygon bbox X[${minX.toFixed(3)},${maxX.toFixed(3)}] Y[${minY.toFixed(3)},${maxY.toFixed(3)}], point budget ${pointBudget}`);

	// Frontier selection only touches cheap hierarchy metadata (numPoints,
	// bounding boxes - never real point payloads), so running it TWICE here
	// (once to cheaply estimate point density with the small nominal
	// margin, once more with the final, data-derived reconstruction margin)
	// costs one extra hierarchy-chunk round trip, not a real point fetch.
	const probeBox = makeLocalBox(baseMarginX, baseMarginY);
	const probe = await selectFrontier(root, probeBox, pointBudget);
	const probeArea = Math.max(1e-9, (probeBox.max.x - probeBox.min.x) * (probeBox.max.y - probeBox.min.y));
	const estimatedSpacing = probe.totalPoints > 0 ? Math.sqrt(probeArea / probe.totalPoints) : 0;
	const reconstructionMargin = estimatedSpacing * RECONSTRUCTION_MARGIN_SPACING_MULTIPLIER;
	const marginX = Math.max(baseMarginX, reconstructionMargin);
	const marginY = Math.max(baseMarginY, reconstructionMargin);

	console.log(`${tag} ROI: estimated point spacing ~${estimatedSpacing.toFixed(4)} (from ${Math.round(probe.totalPoints)} points over ${probeArea.toFixed(2)} m² probe area) -> reconstruction margin ${reconstructionMargin.toFixed(4)} m (final margin X=${marginX.toFixed(4)} Y=${marginY.toFixed(4)})`);

	const localBox = makeLocalBox(marginX, marginY);

	const tFrontier0 = performance.now();
	const {frontier, level, totalPoints, levels, budgetExceededAtRoot} = await selectFrontier(root, localBox, pointBudget);
	const frontierMs = performance.now() - tFrontier0;

	console.log(`${tag} ROI frontier: level ${level} (${levels.length} octree levels visited), ${frontier.length} nodes selected, ~${Math.round(totalPoints)} points estimated inside ROI at frontier, ${frontierMs.toFixed(1)} ms`);
	for(const l of levels) console.log(`${tag}   level ${l.level}: ${l.nodeCount} intersecting node(s), ~${l.totalPointsRaw} raw points in those nodes' own full extent, ~${Math.round(l.totalPoints)} estimated inside the ROI`);
	if(budgetExceededAtRoot){
		console.warn(`${tag} ROI: even the root node alone (~${Math.round(totalPoints)} estimated points inside ROI) exceeds the point budget (${pointBudget}) - using the root as-is; consider a larger budget or a smaller polygon.`);
	}

	// `Potree.maxNodesLoading` (default 4) throttles the engine's OWN
	// concurrent node-decode count - sized for smooth incremental streaming
	// while the camera moves, not for a one-shot bulk ROI fetch. Found via
	// live testing: forcing many nodes through a gate of 4 (via this file's
	// own poll-and-retry loop) let individual nodes get starved past
	// `MAX_LOAD_WAIT_MS` under real contention. Temporarily raising the gate
	// for the duration of this one bulk load (always restored afterwards)
	// fixes the actual bottleneck without touching the timeout/poll logic.
	//
	// Audit finding: a plain save/restore-to-one-remembered-value here race-
	// condition when two ROI loads overlap (e.g. a user switches tools mid-
	// load - DigitizationModule.startInsertion() broadcasts `cancel_insertions`
	// on every tool switch, but that only sets the PREVIOUS tool's own
	// `cancelled` flag; it does not abort an already-in-flight
	// loadFrontierPoints() call, so a second loadRoiPoints() can genuinely
	// start before the first one's `finally` below has run). Whichever load
	// finished first would restore the gate back down while the other was
	// still relying on it staying raised. `raiseDepth` makes this a proper
	// nested raise/restore: only the LAST overlapping load to finish
	// restores the gate, and only to the value that was true before ANY of
	// them started.
	if(raiseDepth === 0) valueBeforeAnyRaise = Potree.maxNodesLoading;
	raiseDepth++;
	Potree.maxNodesLoading = Math.max(Potree.maxNodesLoading, 64);

	const tLoad0 = performance.now();
	try{
		await loadFrontierPoints(frontier);
	}finally{
		raiseDepth--;
		if(raiseDepth === 0) Potree.maxNodesLoading = valueBeforeAnyRaise;
	}
	const loadMs = performance.now() - tLoad0;

	const flat = [];
	const flatColors = [];
	let nodePointCount = 0, bboxFilteredPointCount = 0;
	const hidden = hiddenClassLookup(hiddenClasses);
	const classFilter = hidden ? {hidden, excluded: new Set()} : null;
	for(const node of frontier){
		const {nodePoints, keptPoints} = collectNodePoints(node, offset, localBox, flat, flatColors, classFilter);
		nodePointCount += nodePoints;
		bboxFilteredPointCount += keptPoints;
	}

	const positions = Float64Array.from(flat);
	const colors = Uint8Array.from(flatColors);
	const ms = performance.now() - t0;

	console.log(`${tag} ROI: ${frontier.length} nodes loaded in ${loadMs.toFixed(1)} ms, ${nodePointCount} points in loaded nodes, ${bboxFilteredPointCount} points after bbox filter, ${positions.byteLength} bytes to transfer, ROI total ${ms.toFixed(1)} ms`);

	return {
		positions,
		colors,
		nodesLoaded: frontier.length,
		nodesConsidered: levels.reduce((sum, l) => sum + l.nodeCount, 0),
		frontierLevel: level,
		budgetExceededAtRoot,
		pointBudget,
		nodePointCount,
		bboxFilteredPointCount,
		rawPointCount: positions.length / 3,
		excludedClasses: classFilter ? [...classFilter.excluded].sort((a, b) => a - b) : [],
		ms,
	};
}
